import React, {useCallback, useMemo, useState} from 'react';
import {any, arrayOf, exact, func, node, oneOf, oneOfType, string} from 'prop-types';
import cx from 'classnames';
import {GlobalHotKeys} from 'react-hotkeys';
import {ARE_KEYBOARD_SHORTCUTS_ENABLED} from '../../../constant/Application';
import If from '../../misc/If';
import {MODE} from './constant';

import './Select.css';

const propTypes = {
  className : string,
  label     : node,
  mode      : oneOf(Object.values(MODE)),
  onChange  : func,
  options   : arrayOf(exact({
    label  : node,
    value  : any,
    hotKey : string
  })).isRequired,
  value : oneOfType([any, arrayOf(any)])
};

const defaultProps = {
  className : '',
  label     : null,
  mode      : MODE.SINGLE_CHOICE,
  onChange  : () => null,
  value      : null
};

function Select(props) {
  const {className, label, mode, onChange, options, value} = props;
  const [hoverState, setHoverState] = useState({});

  const additionalDescription = useMemo(() => {
    let description = null;

    if (mode === MODE.MULTIPLE_CHOICE) {
      description = 'Choose as many as you like';
    }

    return description;
  }, [mode]);

  const onMouseEnter = useCallback(optionKey => {
    setHoverState(prevState => {
      const nextState = {
        ...prevState,
        [optionKey] : true
      };

      return nextState;
    });
  }, []);

  const onMouseLeave = useCallback(optionKey => {
    setHoverState(prevState => {
      const nextState = {
        ...prevState,
        [optionKey] : false
      };

      return nextState;
    });
  }, []);

  const handleOnClick = useCallback(option => {
    if (mode === MODE.SINGLE_CHOICE) {
      onChange(option.value);
    } else {
      const shouldRemove = value.indexOf(option.value) > -1;
      let nextValue = [...value, option.value];

      if (shouldRemove) {
        nextValue = nextValue.filter(item => item !== option.value);
      }

      onChange(nextValue);
    }
  }, [mode, onChange, value]);

  const {handlers, keyMap} = useMemo(() => {
    const hotKeysData = options.reduce((acc, option) => {
      acc.keyMap[option.hotKey] = option.hotKey;
      acc.handlers[option.hotKey] = () => {
        handleOnClick(option);
      };

      return acc;
    }, {
      handlers : {},
      keyMap   : {}
    });

    return hotKeysData;
  }, [handleOnClick, options]);

  return (
    <div className={cx('select', className)}>
      <If condition={ARE_KEYBOARD_SHORTCUTS_ENABLED}>
        {() => (
          <GlobalHotKeys allowChanges handlers={handlers} keyMap={keyMap} />
        )}
      </If>

      {label && (
        <label className="select__label">
          {label}
        </label>
      )}

      {additionalDescription && (
        <label className="select__additional-description">
          {additionalDescription}
        </label>
      )}

      <ul className="select__options">
        {options.map(option => {
          const isHovered = hoverState[option.value] === true;
          let isSelected = option.value === value;

          if (mode === MODE.MULTIPLE_CHOICE) {
            isSelected = value.includes(option.value);
          }

          return (
            <li
              className={cx('select__option', {
                'select__option--is-hovered'  : isHovered,
                'select__option--is-selected' : isSelected
              })}
              key={option.value}
              onClick={() => handleOnClick(option)}
              onMouseEnter={() => onMouseEnter(option.value)}
              onMouseLeave={() => onMouseLeave(option.value)}
            >
              <If condition={ARE_KEYBOARD_SHORTCUTS_ENABLED && option.hotKey}>
                {() => (
                  <div className="select__option__hot-key__wrap">
                    <span className="select__option__hot-key">
                      {option.hotKey.toUpperCase()}
                    </span>
                  </div>
                )}
              </If>

              <span className="select__option__text">
                {option.label}
              </span>

              {isSelected && (
                <svg className="select__option__icon" height="13" width="16">
                  <path d="M14.293.293l1.414 1.414L5 12.414.293 7.707l1.414-1.414L5 9.586z" fill="currentColor" />
                </svg>
              )}
            </li>
          );
        })}
      </ul>

      <p className="select__message_hidden">
          message
      </p>
    </div>
  );
}

Select.propTypes = propTypes;
Select.defaultProps = defaultProps;
Select.MODE = MODE;

export default Select;
